// Membership Report tab (MingZe only): members ranked by spending, expandable purchase history.
import React from "react";
import { Alert, Button, Col, Input, Label, Row, Spinner, Table } from "reactstrap";
import { PageShell } from "./memberComponents.jsx";
import { formatDate, formatPhone, isOwner, memberPost, money } from "./memberApi.js";

// Group salestable rows (one per product) into invoices.
const groupInvoices = (rows) => {
  const map = {};
  const order = [];
  rows.forEach(r => {
    if (!map[r.invoicenumber]) {
      map[r.invoicenumber] = { invoicenumber: r.invoicenumber, invoicedate: r.invoicedate, dateCreate: r.dateCreate, salesperson: r.salesperson, total: 0, items: [] };
      order.push(r.invoicenumber);
    }
    map[r.invoicenumber].items.push(r);
    map[r.invoicenumber].total += parseFloat(r.subtotal) || 0;
  });
  return order.map(k => map[k]);
};

class MembershipReport extends React.Component {
  state = { report: [], loading: true, error: '', datefrom: '', dateto: '', search: '', open: {}, history: {} };

  componentDidMount() { if (isOwner()) this.load(); }

  load = () => {
    this.setState({ loading: true, open: {} });
    memberPost('member-report', { datefrom: this.state.datefrom, dateto: this.state.dateto }).then(json => this.setState({
      loading: false, report: json.success == 1 ? json.report : [], error: json.success == 1 ? '' : json.message,
    }));
  };

  toggle = (memberid) => {
    const open = Object.assign({}, this.state.open, { [memberid]: !this.state.open[memberid] });
    this.setState({ open });
    if (open[memberid] && !this.state.history[memberid]) {
      memberPost('member-history', { memberid }).then(json => this.setState({
        history: Object.assign({}, this.state.history, { [memberid]: json.success == 1 ? groupInvoices(json.history) : [] }),
      }));
    }
  };

  renderHistory(memberid) {
    const invoices = this.state.history[memberid];
    if (!invoices) return <div className="text-center py-3"><Spinner size="sm" /></div>;
    if (invoices.length === 0) return <div className="text-muted py-2">No purchases yet.</div>;
    return invoices.map(inv => (
      <div key={inv.invoicenumber} className="mb-3" style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 6 }}>
        <div className="d-flex justify-content-between px-3 py-2" style={{ background: '#f6f9fc' }}>
          <span><strong>Invoice {inv.invoicenumber}</strong> · {inv.invoicedate || formatDate(inv.dateCreate)} · {inv.salesperson}</span>
          <strong>{money(inv.total)}</strong>
        </div>
        <Table size="sm" className="mb-0">
          <thead><tr><th>Category</th><th>Product</th><th>IMEI</th><th>Qty</th><th>Unit Price</th><th>Subtotal</th></tr></thead>
          <tbody>{inv.items.map((it, i) => (
            <tr key={i}><td>{it.category}</td><td>{it.productname}</td><td>{it.imei}</td><td>{it.quantity}</td><td>{money(it.unitprice)}</td><td>{money(it.subtotal)}</td></tr>
          ))}</tbody>
        </Table>
      </div>
    ));
  }

  render() {
    const term = this.state.search.trim().toLowerCase();
    const list = this.state.report.filter(r => !term || (r.memberid + ' ' + r.name + ' ' + r.phone).toLowerCase().indexOf(term) !== -1);
    const grand = this.state.report.reduce((sum, r) => sum + (parseFloat(r.totalspent) || 0), 0);
    const buyers = this.state.report.filter(r => parseInt(r.invoicecount, 10) > 0).length;
    return (
      <PageShell title="Membership Report" ownerOnly>
        <Row className="align-items-end mb-3">
          <Col md="3"><Label className="form-control-label">From</Label><Input type="date" value={this.state.datefrom} onChange={e => this.setState({ datefrom: e.target.value })} /></Col>
          <Col md="3"><Label className="form-control-label">To</Label><Input type="date" value={this.state.dateto} onChange={e => this.setState({ dateto: e.target.value })} /></Col>
          <Col md="2" className="mt-2"><Button color="primary" onClick={this.load}>Apply</Button></Col>
          <Col md="4" className="mt-2"><Input placeholder="Search member" value={this.state.search} onChange={e => this.setState({ search: e.target.value })} /></Col>
        </Row>
        <Row className="mb-3 text-center">
          <Col><h2 className="mb-0">{this.state.report.length}</h2><small className="text-muted">Members</small></Col>
          <Col><h2 className="mb-0">{buyers}</h2><small className="text-muted">Members who purchased</small></Col>
          <Col><h2 className="mb-0">{money(grand)}</h2><small className="text-muted">Total member sales</small></Col>
        </Row>
        {this.state.error ? <Alert color="danger">{this.state.error}</Alert> : null}
        <Table size="sm" responsive className="align-items-center">
          <thead className="thead-light">
            <tr><th style={{ width: 50 }}>#</th><th>Member ID</th><th>Name</th><th>Phone</th><th className="text-right">Invoices</th><th className="text-right">Total Purchase</th><th>Last Purchase</th><th style={{ width: 30 }}></th></tr>
          </thead>
          <tbody>
            {this.state.loading ? <tr><td colSpan="8" className="text-center py-4"><Spinner /></td></tr> :
              list.length === 0 ? <tr><td colSpan="8" className="text-center text-muted py-4">No members</td></tr> :
                list.map(r => {
                  const rank = this.state.report.indexOf(r) + 1;
                  const isOpen = !!this.state.open[r.memberid];
                  return (
                    <React.Fragment key={r.memberid}>
                      <tr className="member-collapse-row" onClick={() => this.toggle(r.memberid)}>
                        <td>{rank <= 3 && parseFloat(r.totalspent) > 0 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}</td>
                        <td><strong>{r.memberid}</strong></td>
                        <td>{r.name}</td>
                        <td>{formatPhone(r.phone)}</td>
                        <td className="text-right">{r.invoicecount}</td>
                        <td className="text-right"><strong>{money(r.totalspent)}</strong></td>
                        <td>{formatDate(r.lastpurchase)}</td>
                        <td>{isOpen ? '▲' : '▼'}</td>
                      </tr>
                      {isOpen ? <tr><td colSpan="8" style={{ background: '#fafbfd' }}>{this.renderHistory(r.memberid)}</td></tr> : null}
                    </React.Fragment>
                  );
                })}
          </tbody>
        </Table>
        <small className="text-muted">Totals count only sales keyed in with the member ID after the member registered.</small>
      </PageShell>
    );
  }
}

export default MembershipReport;
